// Block identity and similarity for alignment (RES-561 compare feature).

import { isSkipDiff } from './normalise';

// Concatenated leaf text of a node/array. Element children are joined with a
// newline so block boundaries can't collapse ("ab"+"c" vs "a"+"bc" must differ).
export function plainText(node) {
	if (Array.isArray(node))
		return node.map(plainText).join('\n');
	if (!node || typeof node !== 'object')
		return '';
	if (typeof node.text === 'string' && node.type === undefined)
		return node.text;
	if (!Array.isArray(node.children))
		return '';
	const hasElementChildren = node.children.some((c) => c && c.type !== undefined);
	return node.children.map(plainText).join(hasElementChildren ? '\n' : '');
}

// djb2 — cheap, stable, good enough for anchor equality.
export function hash(str) {
	let h = 5381;
	for (let i = 0; i < str.length; i++)
		h = ((h << 5) + h + str.charCodeAt(i)) | 0;
	return (h >>> 0).toString(36);
}

// Exact-match signature used as LCS anchors: type + content identity. Formatting-only
// changes (bold toggled, table restyled) intentionally produce the same signature.
export function blockSignature(node) {
	if (!node || typeof node !== 'object')
		return 'null';
	const type = node.type || 'text';
	// Engine-generated subtrees anchor to each other positionally regardless of their
	// (regenerated) content, so they are never reported as document changes.
	if (isSkipDiff(node))
		return type + '|skip';
	if (type === 'image-wrapper')
		return type + '|' + imageIdentity(node);
	if (type === 'parameter')
		return type + '|' + (node.parameterKey || '');
	if (type === 'parameter-component')
		return type + '|' + (node.componentKey || '');
	return type + '|' + hash(plainText(node));
}

function imageIdentity(node) {
	const image = (node.children || []).find((c) => c.type === 'image');
	return hash((image && image.url) || '') + '|' + (node.imageWidth || '');
}

// Token-Dice similarity over word multisets — 0..1, used to pair same-type blocks
// that were edited (threshold applied by alignBlocks).
export function similarity(a, b) {
	const ta = tokens(plainText(a));
	const tb = tokens(plainText(b));
	if (ta.length === 0 && tb.length === 0)
		return 1;
	if (ta.length === 0 || tb.length === 0)
		return 0;
	const counts = new Map();
	ta.forEach((t) => counts.set(t, (counts.get(t) || 0) + 1));
	let overlap = 0;
	tb.forEach((t) => {
		const c = counts.get(t);
		if (c > 0) {
			overlap++;
			counts.set(t, c - 1);
		}
	});
	return (2 * overlap) / (ta.length + tb.length);
}

function tokens(text) {
	return text.toLowerCase().split(/\s+/).filter(Boolean);
}
